/**
 * Octoplus style helpers (design-v2). Pure functions returning plain
 * `sx`-compatible objects — no component, no global CSS.
 *
 *   const t = useOctoTokens();
 *   <Box sx={{ ...octoRootSx(t) }}>…</Box>          // chat root (P1)
 *   <ButtonBase sx={{ ...octoFocusRing(t) }} />     // any custom control
 *   <Typography sx={octoText(t, 'caption')} />
 */
import type { OctoColorTokens, OctoTokens } from './octoTokens';
import { octoCssVars, octoType } from './octoTokens';
import { statusShimmer } from '../ui/keyframes';

type Sx = Record<string, unknown>;

/* ------------------------------- Focus -------------------------------- */

/** Root-level guard: rings draw only while the reader navigates by keyboard (ui/inputModality). */
// Starts with a type selector: stylis prefixes keys that begin with ':' with the parent class.
export const OCTO_KEYBOARD = 'html[data-octo-modality="keyboard"]';

/**
 * Selector key that applies only in keyboard modality. Each comma-separated
 * part is prefixed: `kbFocus('&.Mui-focusVisible, &:focus-visible')`.
 */
export const kbFocus = (sel = '&:focus-visible'): string =>
  sel
    .split(',')
    .map((part) => `${OCTO_KEYBOARD} ${part.trim()}`)
    .join(', ');

/** The ring itself (2px `ink`, offset 2 — or inset with a negative offset). */
export const octoRing = (t: OctoTokens, offset = 2): Sx => ({
  outline: `2px solid ${t.color.ink}`,
  outlineOffset: `${offset}px`,
});

/** Focus ring on a control: 2px `ink`, only for keyboard navigation. */
export function octoFocusRing(t: OctoTokens): Sx {
  return {
    '&:focus, &:focus-visible': { outline: 'none' },
    [kbFocus()]: octoRing(t),
  };
}

/**
 * Interactive controls that may show a ring. Containers, regions, the
 * message list, the panel and text fields are never in this list: they never
 * draw a ring, whatever focused them.
 */
const RING_TARGETS =
  'button, a[href], select, summary, [role="button"], [role="link"], [role="menuitem"], ' +
  '[role="menuitemradio"], [role="option"], [role="tab"], [role="switch"], [role="checkbox"], ' +
  '[role="radio"], [role="slider"]';

/**
 * Same ring for descendants (apply once on the chat root). Nothing inside the
 * chat draws the browser's default outline; only interactive controls draw
 * the ink ring, and only when they are `:focus-visible` while the reader
 * navigates by keyboard. Text fields show focus through their container
 * (composer pill), never as a rectangle of their own.
 */
export function octoFocusRingWithin(t: OctoTokens): Sx {
  return {
    '&:focus, &:focus-visible, & :focus, & :focus-visible': { outline: 'none' },
    [kbFocus(`& :is(${RING_TARGETS}):focus-visible`)]: octoRing(t),
  };
}

/** `::selection` in `ink-tint` with body text (contrast ≥13:1 both themes). */
export function octoSelection(t: OctoTokens): Sx {
  const sel = { backgroundColor: t.color.inkTint, color: t.color.text };
  return { '&::selection': sel, '& ::selection': sel };
}

/**
 * Chat scope root: `--oc-*` vars + selection + descendant focus ring + body
 * type/colour. Does NOT set a background (the shell decides bg vs surface).
 */
export function octoRootSx(t: OctoTokens): Sx {
  return {
    ...octoCssVars(t.color),
    color: t.color.text,
    ...octoSelection(t),
    ...octoFocusRingWithin(t),
  };
}

/* --------------------------- §4 · Tipografia --------------------------- */

export type OctoTextRole =
  | 'body'
  | 'bodyExpanded'
  | 'greeting'
  | 'h2'
  | 'h3'
  | 'caption'
  | 'step'
  | 'stepCurrent'
  | 'plate';

const ROLE_COLOR: Record<OctoTextRole, keyof OctoColorTokens> = {
  body: 'text',
  bodyExpanded: 'text',
  greeting: 'text',
  h2: 'text',
  h3: 'text',
  caption: 'text3',
  step: 'text2',
  stepCurrent: 'text',
  plate: 'plateFg',
};

/**
 * Typography scale as `sx` (size, line-height, weight, colour). Pass
 * `{ color: false }` to keep the inherited colour.
 */
export function octoText(
  t: OctoTokens,
  role: OctoTextRole,
  opts: { color?: boolean } = {},
): Sx {
  const base: Sx = (() => {
    switch (role) {
      case 'body':
        return { ...octoType.body, fontWeight: 400 };
      case 'bodyExpanded':
        return { ...octoType.bodyExpanded, fontWeight: 400 };
      case 'greeting':
        return { ...octoType.greeting };
      case 'h2':
        return { ...octoType.h2 };
      case 'h3':
        return { ...octoType.h3 };
      case 'caption':
        return { ...octoType.caption, fontWeight: 400 };
      case 'step':
        return { ...octoType.step, fontWeight: 400 };
      case 'stepCurrent':
        return { ...octoType.step, fontWeight: 500 };
      case 'plate':
        return {
          ...octoType.plate,
          fontFamily: octoType.mono,
          fontVariantNumeric: 'tabular-nums',
        };
      default:
        return {};
    }
  })();
  return opts.color === false ? base : { ...base, color: t.color[ROLE_COLOR[role]] };
}

/** `tabular-nums` for tables, counters, "antes → depois". */
export const octoTabularNums: Sx = { fontVariantNumeric: 'tabular-nums' };

/** Screen-reader-only text (keeps the node in the a11y tree). */
export const octoVisuallyHidden: Sx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** `@media (prefers-reduced-motion: reduce)` key for sx objects. */
export const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

/**
 * Status shimmer (working / transcribing): a soft light band travels across
 * the text. `color` stays the solid base colour (what axe measures and what
 * reduced motion shows); the band is painted through `background-clip: text`.
 * Put nothing that needs its own colour inside this node (the transparent
 * fill is inherited) — render counters as a sibling.
 */
export function octoShimmerTextSx(
  t: OctoTokens,
  base: string = t.color.text2,
  hi: string = base === t.color.text ? t.color.text3 : t.color.text,
): Sx {
  return {
    color: base,
    backgroundImage: `linear-gradient(90deg, ${base} 0%, ${base} 38%, ${hi} 50%, ${base} 62%, ${base} 100%)`,
    backgroundSize: '250% 100%',
    backgroundRepeat: 'no-repeat',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: `${statusShimmer} 2200ms linear infinite`,
    [REDUCED_MOTION]: {
      animation: 'none',
      backgroundImage: 'none',
      WebkitTextFillColor: 'currentColor',
    },
  };
}

/* --------------------------- Markdown lists --------------------------- */

/**
 * Bullet list: plain 5px round markers in `marker` green, no rail (nested
 * lists render through the same helper). Ordered lists keep their numbers.
 *
 *   ul: { ...octoBulletListSx(t) }
 */
export function octoBulletListSx(t: OctoTokens): Sx {
  return {
    listStyle: 'none',
    pl: '20px',
    '& > li': { position: 'relative' },
    '& > li::before': {
      content: '""',
      position: 'absolute',
      left: '-14px',
      top: 'calc(0.8em - 2.5px)',
      width: '5px',
      height: '5px',
      borderRadius: '50%',
      bgcolor: t.color.marker,
    },
  };
}

/* ------------------------ Menus and lists (shared) ----------------------- */

/**
 * ONE style for every menu and list in the chat (model menu, "Mais opções",
 * conversation list, memories, procedures):
 *   surface  radius 12, 4px inner padding, hairline border, soft shadow (light only)
 *   item     radius 8, inset in that padding, padding 8×10, ~36px (one line)
 *            or ~48px (with subtitle)
 *   hover    a very subtle background only (no border, outline or shadow)
 *   selected the same shape, slightly stronger background; title 600
 *   pressed  a brief, slightly stronger background
 *   focus    the standard 2px ring
 */
export function octoMenuPaperSx(t: OctoTokens): Sx {
  return {
    p: '4px',
    bgcolor: t.color.raised,
    backgroundImage: 'none',
    color: t.color.text,
    border: `1px solid ${t.color.hairline}`,
    borderRadius: '12px',
    boxShadow: t.isDark ? 'none' : '0 8px 24px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04)',
    '& .MuiList-root, & .MuiMenu-list': { p: 0 },
  };
}

export function octoItemSx(
  t: OctoTokens,
  opts: { selected?: boolean; twoLine?: boolean } = {},
): Sx {
  const { selected = false, twoLine = false } = opts;
  const c = t.color;
  const base = selected ? c.itemSelected : 'transparent';
  return {
    borderRadius: '8px',
    minHeight: twoLine ? 48 : 36,
    px: '10px',
    py: '8px',
    border: 0,
    outline: 'none',
    boxShadow: 'none',
    bgcolor: base,
    color: c.text,
    transition: `background-color ${t.motion.hover}ms ${t.motion.easeInk}`,
    '& + &': { mt: '2px' },
    '&.Mui-selected, &.Mui-focusVisible': { bgcolor: base },
    '&:hover': { bgcolor: selected ? c.itemSelected : c.itemHover },
    '&.Mui-selected:hover': { bgcolor: c.itemSelected },
    '&:active': { bgcolor: c.itemPressed },
    '&:focus-visible': { outline: 'none' },
    [kbFocus('&.Mui-focusVisible, &:focus-visible')]: octoRing(t, -2),
    '& .MuiTouchRipple-root': { display: 'none' },
  };
}
